import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, authHeaders } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const EMPTY = {
  title: "", tag: "Free and open to the public", description: "", start: "", end: "", location: "",
  directions_url: "", expected: "", offerings: "", partner_note: "", partners: "", cta_label: "", cta_url: "",
};
const inputCls = "mt-1.5 bg-[#F6F1FD] border-[#E4DCF2] focus-visible:ring-[#7B3FE4]";

const Field = ({ id, label, hint, children, className = "" }) => (
  <div className={className}>
    <Label htmlFor={id} className="font-bold">{label}</Label>
    {children}
    {hint && <p className="text-xs text-[#5E5672] mt-1">{hint}</p>}
  </div>
);

export const EventForm = ({ onCreated, onError }) => {
  const [f, setF] = useState(EMPTY);
  const [flyer, setFlyer] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const text = (k, label, props = {}) => (
    <Input id={`ev-${k}`} value={f[k]} onChange={set(k)} className={inputCls} data-testid={`event-form-${k.replace(/_/g, "-")}-input`} {...props} />
  );
  const area = (k, rows = 3) => (
    <Textarea id={`ev-${k}`} rows={rows} value={f[k]} onChange={set(k)} className={inputCls} data-testid={`event-form-${k.replace(/_/g, "-")}-input`} />
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.start) return toast.error("Title and start date/time are required");
    if (f.end && new Date(f.end) < new Date(f.start)) return toast.error("End must be after start");
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => {
      if (k === "start" || k === "end") { if (v) fd.append(k, new Date(v).toISOString()); }
      else fd.append(k, v);
    });
    if (flyer) fd.append("flyer", flyer);
    setBusy(true);
    try {
      await axios.post(`${API}/admin/events`, fd, { headers: authHeaders() });
      toast.success("Event added");
      setF(EMPTY);
      setFlyer(null);
      onCreated();
    } catch (err) { onError(err); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#E4DCF2] rounded-2xl p-5 sm:p-7 mb-8 grid gap-5 sm:grid-cols-2" data-testid="admin-event-form">
      <h3 className="font-display text-2xl sm:col-span-2">New event</h3>
      <Field id="ev-title" label="Title *" className="sm:col-span-2">{text("title")}</Field>
      <Field id="ev-tag" label="Tag" hint='e.g. "Free and open to the public"'>{text("tag")}</Field>
      <Field id="ev-expected" label="Expected" hint='e.g. "200–300 neighbors"'>{text("expected")}</Field>
      <Field id="ev-description" label="Description" className="sm:col-span-2">{area("description")}</Field>
      <Field id="ev-start" label="Start date & time *" hint="Your local time">{text("start", "", { type: "datetime-local" })}</Field>
      <Field id="ev-end" label="End date & time (optional)">{text("end", "", { type: "datetime-local" })}</Field>
      <Field id="ev-location" label="Where" hint="One line per address line">{area("location")}</Field>
      <Field id="ev-directions_url" label="Directions URL" hint="Google Maps link">{text("directions_url", "", { placeholder: "https://maps.google.com/..." })}</Field>
      <Field id="ev-offerings" label="Offerings" hint="One per line">{area("offerings", 5)}</Field>
      <Field id="ev-partners" label="Partners" hint="One name per line">{area("partners", 5)}</Field>
      <Field id="ev-partner_note" label="Partner note" hint="Wrap text in **double asterisks** to bold it" className="sm:col-span-2">{area("partner_note", 2)}</Field>
      <Field id="ev-cta_label" label="CTA label (optional)" hint='e.g. "Become a vendor or partner"'>{text("cta_label")}</Field>
      <Field id="ev-cta_url" label="CTA link (optional)" hint="https://… or mailto:…">{text("cta_url")}</Field>
      <Field id="ev-flyer" label="Flyer image" hint="JPG/PNG, up to 10MB" className="sm:col-span-2">
        <Input id="ev-flyer" type="file" accept="image/*" onChange={(e) => setFlyer(e.target.files?.[0] || null)} className={inputCls} data-testid="event-form-flyer-input" />
        {flyer && <img src={URL.createObjectURL(flyer)} alt="Flyer preview" className="mt-3 w-32 rounded-lg border border-[#E4DCF2]" data-testid="event-form-flyer-preview" />}
      </Field>
      <div className="sm:col-span-2 flex justify-end">
        <Button type="submit" disabled={busy} className="rounded-full bg-[#F4B41A] text-[#170B2E] hover:bg-[#FFC93C] font-extrabold h-11 px-6" data-testid="event-form-submit-button">
          {busy ? "Saving…" : "Save event"}
        </Button>
      </div>
    </form>
  );
};
